import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Trash2 } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import Select from 'src/components/input/select/Select';
import { usePropertyFactMutations } from 'src/services/api/properties/use-property-fact-mutations';
import type { IProperty, IPropertyFact } from 'src/services/api/properties/types';
import { isBooleanFactKey, isHiddenFactKey, PROPERTY_FACT_KEYS, PROPERTY_FACT_KEY_LABELS, type PropertyFactKey } from 'src/services/api/properties/property-fact-keys';
import type { PropertyFactValue } from 'src/services/api/properties/property-fact-values';
import {
    BOOLEAN_FACT_OPTIONS,
    factToFormValue,
    formValueToApiValue,
    getFactValueError,
    isEditableFact,
} from 'src/components/create-or-update-property-modal/property-fact-form-values';
import { isStandardErrorResponse } from 'src/api/common';
import { getApiErrorMessage } from 'src/services/api/utils';
import { useNotifications } from 'src/notifications';

import styles from 'src/components/create-or-update-property-modal/PropertyFactsSection.module.css';

export interface PropertyFactsSectionRef {
    applyChanges: () => Promise<boolean>;
    isDirty: () => boolean;
}

interface PropertyFactFormRow {
    factId: number | null;
    key: PropertyFactKey | '';
    value: string;
    originalValue: string;
    deleted: boolean;
}

interface PropertyFactsForm {
    facts: PropertyFactFormRow[];
}

interface Props {
    property: IProperty;
}

type FactOperation =
    | { type: 'create'; rowIndex: number; key: PropertyFactKey; value: PropertyFactValue }
    | { type: 'update'; rowIndex: number; factId: number; value: PropertyFactValue }
    | { type: 'delete'; rowIndex: number; factId: number };

type FactOperationResult =
    | { type: 'create' | 'update'; rowIndex: number; fact: IPropertyFact }
    | { type: 'delete'; rowIndex: number };

const FACT_VALIDATION_ERROR = 'Fix the highlighted property facts before saving.';
const FACT_SAVE_ERROR = 'Failed to save property facts. Property was not updated.';

function factToFormRow(fact: IPropertyFact): PropertyFactFormRow {
    const value = factToFormValue(fact) ?? '';

    return {
        factId: fact.id,
        key: fact.key,
        value,
        originalValue: value,
        deleted: false,
    };
}

function getDefaultValues(property: IProperty): PropertyFactsForm {
    return {
        facts: property.facts ?
            Object.values(property.facts).filter(isEditableFact).map(factToFormRow) :
            [],
    };
}

function getFactSaveErrorMessage(error: unknown): string {
    if (isStandardErrorResponse(error)) {
        return getApiErrorMessage(error) || FACT_SAVE_ERROR;
    }

    if (Array.isArray(error)) {
        const standardErrors = error.filter(isStandardErrorResponse);
        if (standardErrors.length > 0) {
            return getApiErrorMessage(standardErrors) || FACT_SAVE_ERROR;
        }

        const errorMessages = Array.from(new Set(error.flatMap((item) => {
            return item instanceof Error && item.message ? [item.message] : [];
        })));

        return errorMessages.join(', ') || FACT_SAVE_ERROR;
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return FACT_SAVE_ERROR;
}

const PropertyFactsSection = forwardRef<PropertyFactsSectionRef, Props>(
    function PropertyFactsSection({ property }, ref) {
        const defaultValues = useMemo(() => getDefaultValues(property), [property.id, property.facts]);
        const form = useForm<PropertyFactsForm>({
            mode: 'onBlur',
            defaultValues,
        });
        const { control, clearErrors, formState, getValues, reset, setValue, trigger, watch } = form;
        const { fields, append, remove: removeRow } = useFieldArray({
            control,
            name: 'facts',
        });
        const { create, update, remove: removeFact } = usePropertyFactMutations(property.id);
        const { addNotification } = useNotifications();
        const [saveError, setSaveError] = useState<string | null>(null);
        const [isSaving, setIsSaving] = useState(false);

        useEffect(() => {
            reset(defaultValues);
            setSaveError(null);
        }, [defaultValues, reset]);

        const watchedRows = watch('facts');
        const visibleRows = watchedRows.filter((row) => !row.deleted);
        const hiddenFactKeys = property.facts ?
            Object.values(property.facts)
                .filter((fact) => !isEditableFact(fact))
                .map((fact) => fact.key) :
            [];
        const reservedKeys = new Set<PropertyFactKey>([
            ...hiddenFactKeys,
            ...watchedRows
                .filter((row) => !row.deleted)
                .map((row) => row.key)
                .filter((key): key is PropertyFactKey => Boolean(key)),
        ]);
        const canAddRow = PROPERTY_FACT_KEYS.some((key) => !isHiddenFactKey(key) && !reservedKeys.has(key)) &&
            visibleRows.every((row) => Boolean(row.key));

        const hasUnsavedChanges = watchedRows.some((row) => {
            if (row.factId == null) {
                return !row.deleted;
            }

            return row.deleted || row.value !== row.originalValue;
        });

        const getKeyOptions = useCallback((currentKey: PropertyFactKey | '') => {
            return Object.entries(PROPERTY_FACT_KEY_LABELS)
                .filter(([key]) => !isHiddenFactKey(key as PropertyFactKey) &&
                    (key === currentKey || !reservedKeys.has(key as PropertyFactKey)))
                .map(([key, label]) => ({ value: key as PropertyFactKey, label }));
        }, [reservedKeys]);

        const validateFactKey = useCallback((value: PropertyFactKey | '', index: number) => {
            const rows = getValues('facts');
            const row = rows[index];

            if (!row || row.deleted) {
                return true;
            }

            if (!value) {
                return 'Fact is required';
            }

            const duplicateCount = rows.filter((candidate) => {
                return !candidate.deleted && candidate.key === value;
            }).length;

            return duplicateCount <= 1 || 'Fact is already selected';
        }, [getValues]);

        const validateFactValue = useCallback((value: string, index: number) => {
            const row = getValues(`facts.${index}`);

            if (!row || row.deleted) {
                return true;
            }

            return getFactValueError(row.key, value) ?? true;
        }, [getValues]);

        const buildOperations = useCallback((values: PropertyFactsForm): FactOperation[] => {
            return values.facts.flatMap((row, rowIndex): FactOperation[] => {
                if (row.factId != null && row.deleted) {
                    return [{ type: 'delete', rowIndex, factId: row.factId }];
                }

                if (row.deleted) {
                    return [];
                }

                const key = row.key as PropertyFactKey;
                const value = formValueToApiValue(key, row.value);

                if (row.factId == null) {
                    return [{ type: 'create', rowIndex, key, value }];
                }

                if (row.value !== row.originalValue) {
                    return [{ type: 'update', rowIndex, factId: row.factId, value }];
                }

                return [];
            });
        }, []);

        const runOperation = useCallback(async (operation: FactOperation): Promise<FactOperationResult> => {
            switch (operation.type) {
                case 'create':
                    return {
                        type: 'create',
                        rowIndex: operation.rowIndex,
                        fact: await create.mutateAsync({
                            key: operation.key,
                            value: operation.value,
                        }),
                    };
                case 'update':
                    return {
                        type: 'update',
                        rowIndex: operation.rowIndex,
                        fact: await update.mutateAsync({
                            factId: operation.factId,
                            value: operation.value,
                        }),
                    };
                case 'delete':
                    await removeFact.mutateAsync({ factId: operation.factId });
                    return {
                        type: 'delete',
                        rowIndex: operation.rowIndex,
                    };
            }
        }, [create, update, removeFact]);

        const resetSavedRows = useCallback((values: PropertyFactsForm, results: FactOperationResult[]) => {
            const savedFactsByRowIndex = new Map<number, IPropertyFact>();
            const deletedRowIndexes = new Set<number>();

            for (const result of results) {
                if (result.type === 'delete') {
                    deletedRowIndexes.add(result.rowIndex);
                } else {
                    savedFactsByRowIndex.set(result.rowIndex, result.fact);
                }
            }

            reset({
                facts: values.facts.flatMap((row, rowIndex): PropertyFactFormRow[] => {
                    if (deletedRowIndexes.has(rowIndex) || row.deleted) {
                        return [];
                    }

                    const savedFact = savedFactsByRowIndex.get(rowIndex);
                    if (savedFact && isEditableFact(savedFact)) {
                        return [factToFormRow(savedFact)];
                    }

                    if (savedFact) {
                        return [];
                    }

                    return [{
                        factId: row.factId,
                        key: row.key,
                        value: row.value,
                        originalValue: row.value,
                        deleted: false,
                    }];
                }),
            });
        }, [reset]);

        const applyChanges = useCallback(async () => {
            setSaveError(null);

            const isValid = await trigger();
            if (!isValid) {
                setSaveError(FACT_VALIDATION_ERROR);
                addNotification(FACT_VALIDATION_ERROR, 'error');
                return false;
            }

            const values = getValues();
            const operations = buildOperations(values);

            if (operations.length === 0) {
                addNotification('Property facts are already up to date', 'success');
                return true;
            }

            setIsSaving(true);
            try {
                const results = await Promise.allSettled(operations.map(runOperation));
                const successfulResults = results.flatMap((result): FactOperationResult[] => {
                    return result.status === 'fulfilled' ? [result.value] : [];
                });
                const errors = results.flatMap((result): unknown[] => {
                    return result.status === 'rejected' ? [result.reason] : [];
                });

                if (successfulResults.length > 0) {
                    resetSavedRows(values, successfulResults);
                }

                if (errors.length > 0) {
                    const errorMessage = getFactSaveErrorMessage(errors);
                    setSaveError(errorMessage);
                    addNotification(errorMessage, 'error');
                    return false;
                }

                addNotification('Property facts saved', 'success');
                return true;
            } finally {
                setIsSaving(false);
            }
        }, [addNotification, buildOperations, getValues, resetSavedRows, runOperation, trigger]);

        useImperativeHandle(ref, () => ({
            applyChanges,
            isDirty: () => hasUnsavedChanges || formState.isDirty,
        }), [applyChanges, formState.isDirty, hasUnsavedChanges]);

        const handleAddRow = useCallback(() => {
            setSaveError(null);
            append({
                factId: null,
                key: '',
                value: '',
                originalValue: '',
                deleted: false,
            });
        }, [append]);

        const handleDeleteRow = useCallback((index: number) => {
            setSaveError(null);
            const row = getValues(`facts.${index}`);

            if (!row) {
                return;
            }

            if (row.factId == null) {
                removeRow(index);
                return;
            }

            setValue(`facts.${index}.deleted`, true, {
                shouldDirty: true,
                shouldValidate: true,
            });
            clearErrors(`facts.${index}`);
        }, [clearErrors, getValues, removeRow, setValue]);

        return (
            <form onSubmit={(event) => event.preventDefault()}>
                <FlexColumn rowGap={2}>
                    <div className={styles.header}>
                        <Typography fontWeight={500} data-property-section-title>Property Facts</Typography>
                        {hasUnsavedChanges && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                data-testid="property-facts-dirty-indicator"
                            >
                                Unsaved changes
                            </Typography>
                        )}
                    </div>

                    {saveError && (
                        <Alert severity="error" data-testid="property-facts-error-alert">
                            {saveError}
                        </Alert>
                    )}

                    {fields.map((factField, index) => {
                        const row = watchedRows[index];
                        if (!row || row.deleted) {
                            return null;
                        }

                        const isNewFact = row.factId == null;
                        const rowTestId = row.key || `row-${index}`;

                        return (
                            <div key={factField.id} className={styles.factRow}>
                                {isNewFact ? (
                                    <Controller
                                        name={`facts.${index}.key`}
                                        control={control}
                                        rules={{ validate: (value) => validateFactKey(value, index) }}
                                        render={({ field: controllerField, fieldState }) => (
                                            <Select
                                                label="Fact"
                                                placeholder="Select fact"
                                                options={getKeyOptions(row.key)}
                                                value={controllerField.value}
                                                onChange={(value) => {
                                                    controllerField.onChange(value);
                                                    // the old text can't be valid for the new key's input
                                                    setValue(`facts.${index}.value`, '');
                                                    clearErrors(`facts.${index}.value`);
                                                }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                size="small"
                                                fullWidth
                                                data-testid={`property-fact-key-${rowTestId}`}
                                            />
                                        )}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" className={styles.factLabel}>
                                        {PROPERTY_FACT_KEY_LABELS[row.key as PropertyFactKey]}
                                    </Typography>
                                )}

                                <Controller
                                    name={`facts.${index}.value`}
                                    control={control}
                                    rules={{ validate: (value) => validateFactValue(value, index) }}
                                    render={({ field: controllerField, fieldState }) => (
                                        isBooleanFactKey(row.key) ? (
                                            <Select
                                                label={isNewFact ? 'Value' : undefined}
                                                placeholder="Select Yes or No"
                                                options={BOOLEAN_FACT_OPTIONS}
                                                value={controllerField.value}
                                                onChange={(value) => controllerField.onChange(value)}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                size="small"
                                                fullWidth
                                                data-testid={`property-fact-value-${rowTestId}`}
                                            />
                                        ) : (
                                            <TextField
                                                {...controllerField}
                                                label={isNewFact ? 'Value' : undefined}
                                                size="small"
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                data-testid={`property-fact-value-${rowTestId}`}
                                            />
                                        )
                                    )}
                                />

                                <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => handleDeleteRow(index)}
                                    data-testid={isNewFact ? `delete-new-fact-${rowTestId}` : `delete-fact-${row.key}`}
                                >
                                    <Trash2 size={16} />
                                </IconButton>
                            </div>
                        );
                    })}

                    <Button
                        type="button"
                        variant="outlined"
                        size="medium"
                        fullWidth
                        onClick={handleAddRow}
                        disabled={!canAddRow}
                        className={styles.addRowButton}
                        data-testid="add-property-fact-row-button"
                    >
                        + Add Fact
                    </Button>

                    <div className={styles.actions}>
                        <LoadingButton
                            type="button"
                            className={styles.saveButton}
                            variant="contained"
                            size="small"
                            loading={isSaving}
                            disabled={!hasUnsavedChanges}
                            onClick={applyChanges}
                            data-testid="save-property-facts-button"
                        >
                            Save
                        </LoadingButton>
                    </div>
                </FlexColumn>
            </form>
        );
    }
);

export default PropertyFactsSection;
